// Inventory: Owners (Starchive-Inventory-Section-Plan, section 3.3; the canvas's I4 board). The pieces with no owner
// recorded, marked from the desk: one piece with its buttons, or a run of pieces with the bar at the bottom. Every save is
// the same record QA's whose-piece makes (Shopify tag, Consignify record and share, intake row, audit line), signed with an
// owner ID. 30 Sep: To review (the bulk pass's owners, confirmed one by one or in a run), Masih's % for a Split or Masih
// piece that is not the usual 47 / 97, "Use Consignify's" on a disagreement, and Out of stock for a listing that should
// not be on the floor (duplicate, not here, sold elsewhere).
import { useMemo, useState } from 'react'
import { Screen } from '../ReportsApp'
import { downloadCsv, reportsAction, useReport } from '../api'
import { consignorName, int, money, moneyTile, n } from '../format'
import { AsyncButton, Badge, Button, Tile } from '../ui'
import { PhotoImg, type FloorPiece } from './inventory-ui'
import { PieceDrawer } from './PieceDrawer'
import { ConsignorSelect, OwnerEditor, USUAL, useOwnerSigner, type ConsignorOpt } from './owner-edit'

type OwnerRow = FloorPiece & { ownership_tag: string | null; consignify_share: number | null; tag_hints: string[]; consignify_owner?: string | null }
interface OwnersData {
  tiles: { no_owner: number; no_owner_list: number; with_hints: number; disagree: number; recorded: number; shane: number; masih: number; split: number; consignor_pieces: number; consignor_count: number; to_review?: number; reviewed?: number }
  pieces: OwnerRow[]
  disagree: OwnerRow[]
  review?: OwnerRow[]
  consignors: { id: string; key: string; name: string; default_split: number | null; in_consignify: boolean }[]
}

const OWNERS = ['shane', 'masih', 'split', 'consignor'] as const
const label = (o: string) => o[0].toUpperCase() + o.slice(1)
const OOS_REASONS = [['duplicate', 'Duplicate'], ['not_here', 'Not here'], ['sold', 'Sold elsewhere'], ['untracked', 'Not tracked']] as const
/** What the record says now: "Split (0001, 36%)", with the share called out when it is not the usual one. */
const recorded = (o: string | null | undefined, key: string | null | undefined, share: number | null | undefined) =>
  o === 'consignor' && key
    ? `${consignorName(key)}${share != null ? `, ${n(share)}%` : ''}`
    : `${label(String(o ?? '?'))}${key ? ` (${consignorName(key)}${share != null ? `, ${n(share)}%` : ''})` : ''}`

export function Owners() {
  const { data: d, error, loading, refresh } = useReport<OwnersData>('inv_owners')
  // the owner ID signs every save; kept for this tab so it is typed once. A tap without one asks, then carries on.
  const sg = useOwnerSigner()
  const { ownerId, setOwnerId, signer, signed, idRef } = sg
  // consignors added here ("New consignor…") show at once, before the report is read again
  const [added, setAdded] = useState<ConsignorOpt[]>([])
  const allCons = useMemo(() => [...(d?.consignors ?? []), ...added.filter((a) => !(d?.consignors ?? []).some((c) => c.id === a.id))], [d, added])
  const onAdded = (c: ConsignorOpt) => { setAdded((x) => [...x, c]); refresh() }
  // per-card owner with a share: Split / Masih at another %, or a consignor with theirs
  const [edit, setEdit] = useState<{ id: string; owner: string; consignorId: string } | null>(null)
  const [show, setShow] = useState<'none' | 'hints' | 'disagree' | 'review'>('review')
  const [who, setWho] = useState<string>('all')
  const [oos, setOos] = useState<string | null>(null)
  const [bulkOos, setBulkOos] = useState(false)
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [bulkOwner, setBulkOwner] = useState<string>('shane')
  const [consignorId, setConsignorId] = useState('')
  const [split, setSplit] = useState('')
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const [shown, setShown] = useState(24)

  const rows = useMemo(() => {
    if (show === 'disagree') return d?.disagree ?? []
    if (show === 'review') return (d?.review ?? []).filter((p) => who === 'all' || p.ownership_tag === who)
    let r = d?.pieces ?? []
    if (show === 'hints') r = r.filter((p) => p.tag_hints.length)
    return r
  }, [d, show, who])

  const consignor = allCons.find((c) => c.id === consignorId)
  const needConsignor = bulkOwner === 'consignor'
  const needMasihPct = bulkOwner === 'split' || bulkOwner === 'masih'
  const splitVal = split ? Number(split) : needConsignor && consignor?.default_split != null ? n(consignor.default_split) : null
  const splitBad = split !== '' && !(Number(split) > 0 && Number(split) < 100)

  const mark = async (productId: string, owner: string, opts: { consignorId?: string; pct?: number | null } = {}) => {
    const body: Record<string, unknown> = { product_id: productId, owner, employee_id: idRef.current }
    if (owner === 'consignor') { body.consignor_id = opts.consignorId ?? consignorId; const pct = opts.pct ?? splitVal; if (pct != null) body.split_pct = pct }
    else if ((owner === 'split' || owner === 'masih') && (opts.pct ?? (owner === bulkOwner ? splitVal : null)) != null) body.split_pct = opts.pct ?? splitVal
    await reportsAction('owner_mark', body)
  }
  // Use Consignify's: settle a disagreement on what Consignify holds, share and all (a 36% Split stays 36%)
  const takeConsignify = async (p: OwnerRow) => {
    setMsg('')
    const cfo = String(p.consignify_owner ?? '')
    const c = cfo === 'consignor' ? allCons.find((x) => x.key === p.consignor_key) : undefined
    if (cfo === 'consignor' && !c) { setMsg(`${p.title}: ${consignorName(p.consignor_key)} is not in the consignor list`); return }
    setBusy(p.product_id)
    try { await mark(p.product_id, cfo, { consignorId: c?.id, pct: p.consignify_share != null ? n(p.consignify_share) : null }); refresh() }
    catch (e) { setMsg(`${p.title}: ${(e as Error).message}`) } finally { setBusy('') }
  }
  const confirm = async (ids: string[]) => {
    setMsg('')
    setBusy(ids.length === 1 ? ids[0] : `bulk 1 of 1`)
    try { await reportsAction('owner_confirm', { product_ids: ids, employee_id: idRef.current }); setSel(new Set()); refresh() }
    catch (e) { setMsg((e as Error).message) } finally { setBusy('') }
  }
  const outOfStock = async (ids: string[], reason: string) => {
    setMsg('')
    for (let i = 0; i < ids.length; i++) {
      setBusy(ids.length === 1 ? ids[0] : `bulk ${i + 1} of ${ids.length}`)
      try { await reportsAction('out_of_stock', { product_id: ids[i], reason, employee_id: idRef.current }) }
      catch (e) { setMsg(`Stopped at piece ${i + 1}: ${(e as Error).message}`); break }
    }
    setBusy(''); setOos(null); setBulkOos(false); setSel(new Set()); refresh()
  }
  const saveOne = async (p: OwnerRow, owner: string) => {
    setMsg('')
    setBusy(p.product_id)
    try { await mark(p.product_id, owner); refresh() } catch (e) { setMsg(`${p.title}: ${(e as Error).message}`) } finally { setBusy('') }
  }
  const saveEdit = async (p: OwnerRow, owner: string, cid: string, pct: number | null) => {
    setMsg(''); setBusy(p.product_id)
    try { await mark(p.product_id, owner, { consignorId: cid, pct }); setEdit(null); refresh() } finally { setBusy('') }
  }
  const saveBulk = async () => {
    setMsg('')
    const ids = [...sel]
    for (let i = 0; i < ids.length; i++) {
      setBusy(`bulk ${i + 1} of ${ids.length}`)
      try { await mark(ids[i], bulkOwner) } catch (e) { setMsg(`Stopped at piece ${i + 1}: ${(e as Error).message}`); break }
    }
    setBusy(''); setSel(new Set()); refresh()
  }
  const toggle = (id: string) => setSel((s) => { const x = new Set(s); if (x.has(id)) x.delete(id); else x.add(id); return x })
  const t = d?.tiles

  return (
    <Screen title="Owners" sub="Whose piece is it: check the owners the bulk pass recorded, mark the ones with no owner, settle the ones whose sources disagree"
      right={<>
        <label className="rp-ownerbar"><span>Owner ID</span>
          <input inputMode="numeric" autoComplete="off" value={ownerId} onChange={(e) => setOwnerId(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="••••" aria-label="Owner ID" />
          {ownerId.length === 4 && <Badge tone={signer ? 'green' : 'red'}>{signer ? signer.name : 'Not an owner'}</Badge>}
        </label>
        <AsyncButton run={() => downloadCsv('floor')} doneLabel="Downloaded">Export CSV</AsyncButton>
      </>} wide>
      {error && <div className="rp-error">{error}</div>}
      {msg && <div className="rp-error">{msg}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="To review" tone={t && t.to_review ? 'amber' : 'plain'} value={t ? int(t.to_review ?? 0) : '…'} sub={t ? `Owner set by the bulk pass, not yet checked · ${int(t.reviewed ?? 0)} checked` : ''} />
        <Tile label="Not recorded" tone={t && t.no_owner ? 'red' : 'plain'} value={t ? int(t.no_owner) : '…'} sub={t ? `${moneyTile(t.no_owner_list)} at list · no tag, no Consignify record, no intake` : ''} />
        <Tile label="With an old name tag" tone={t && t.with_hints ? 'amber' : 'plain'} value={t ? int(t.with_hints) : '…'} sub="A legacy tag hints at the owner; read it and mark" />
        <Tile label="Tag and Consignify disagree" tone={t && t.disagree ? 'amber' : 'plain'} value={t ? int(t.disagree) : '…'} sub="Both sides shown; pick the right one" />
        <Tile label="Owner recorded" value={t ? int(t.recorded) : '…'} sub={t ? `Shane ${int(t.shane)} · Masih ${int(t.masih)} · Split ${int(t.split)} · Consignors ${int(t.consignor_pieces)} (${int(t.consignor_count)} people)` : ''} />
      </div>
      <div className="rp-filterbar">
        <div className="rp-filterbar__left">
          {([['review', `To review (${t ? int(t.to_review ?? 0) : '…'})`], ['none', `Not recorded (${t ? int(t.no_owner) : '…'})`], ['hints', `With a name tag (${t ? int(t.with_hints) : '…'})`], ['disagree', `Disagree (${t ? int(t.disagree) : '…'})`]] as const).map(([k, l]) => (
            <button key={k} type="button" className={'rp-pill' + (show === k ? ' is-on' : '')} style={{ paddingRight: 12 }} aria-pressed={show === k} onClick={() => { setShow(k); setSel(new Set()); setShown(24); setOos(null) }}>{l}</button>
          ))}
          {show === 'review' && (
            <div className="rp-bulkbar__seg rp-owho" role="group" aria-label="Recorded owner">
              {(['all', ...OWNERS] as const).map((o) => <button key={o} type="button" aria-pressed={who === o} onClick={() => { setWho(o); setSel(new Set()); setShown(24) }}>{o === 'all' ? 'All' : label(o)} {o === 'all' ? int(d?.review?.length ?? 0) : int((d?.review ?? []).filter((p) => p.ownership_tag === o).length)}</button>)}
            </div>
          )}
        </div>
        <span className="rp-muted rp-count">{loading && !d ? '…' : `${int(rows.length)} pieces · price, high to low`}</span>
      </div>
      <div className="rp-ogrid">
        {rows.slice(0, shown).map((p) => (
          <div key={p.product_id} className={'rp-ocard' + (sel.has(p.product_id) ? ' is-sel' : '')}>
            <button type="button" className="rp-ocard__check" role="checkbox" aria-checked={sel.has(p.product_id)} aria-label="Select" onClick={() => toggle(p.product_id)}>
              {sel.has(p.product_id) && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>}
            </button>
            <div className="rp-ocard__pic" onClick={() => setOpen(p.product_id)}><PhotoImg url={p.image_url} title={p.title} width={320} /></div>
            <div className="rp-ocard__body">
              <span className="rp-ocard__title" onClick={() => setOpen(p.product_id)}>{p.title}</span>
              <span className="rp-sub">{money(p.list_price, true)} · {int(p.days_on_floor)} days{p.size ? ` · Sz ${p.size}` : ''}</span>
              {show === 'review'
                ? <span className={'rp-ocard__hint' + (p.consignify_owner && p.consignify_owner !== p.ownership_tag ? ' rp-ocard__hint--amber' : '')}>
                    Recorded <b>{recorded(p.ownership_tag, p.consignor_key, p.consignify_share)}</b>
                    {p.consignify_share != null && USUAL[String(p.ownership_tag)] != null && n(p.consignify_share) !== USUAL[String(p.ownership_tag)] ? <> · not the usual {USUAL[String(p.ownership_tag)]}%</> : null}
                    {p.tag_hints.length ? <> · tagged '{p.tag_hints.join("', '")}'</> : null}
                  </span>
                : show === 'disagree'
                ? <span className="rp-ocard__hint rp-ocard__hint--amber">Tag says <b>{label(String(p.ownership_tag))}</b>, Consignify says <b>{recorded(p.consignify_owner, p.consignor_key, p.consignify_share)}</b></span>
                : p.tag_hints.length
                  ? <span className="rp-ocard__hint rp-ocard__hint--amber">Tagged '{p.tag_hints.join("', '")}'</span>
                  : <span className="rp-ocard__hint">No tag, no Consignify record, no intake</span>}
              {oos === p.product_id ? (
                <div className="rp-ocard__btns rp-ocard__oos">
                  <span>Take off the floor?</span>
                  {OOS_REASONS.map(([k, l]) => <button key={k} type="button" disabled={!!busy} onClick={() => signed(() => outOfStock([p.product_id], k))}>{busy === p.product_id ? '…' : l}</button>)}
                  <button type="button" className="rp-ocard__link" onClick={() => setOos(null)}>Cancel</button>
                </div>
              ) : (
                <div className="rp-ocard__btns">
                  {show === 'review' && <button type="button" className="rp-ocard__ok" disabled={!!busy} title="The recorded owner is right" onClick={() => signed(() => confirm([p.product_id]))}>{busy === p.product_id ? '…' : 'Correct'}</button>}
                  {show === 'disagree' && p.consignify_owner && <button type="button" className="rp-ocard__ok" disabled={!!busy} title="Tag and record follow Consignify, share included" onClick={() => signed(() => takeConsignify(p))}>{busy === p.product_id ? '…' : `Use Consignify's: ${p.consignify_owner === 'consignor' ? consignorName(p.consignor_key) : label(String(p.consignify_owner))}${p.consignify_share != null ? ` ${n(p.consignify_share)}%` : ''}`}</button>}
                  {(['shane', 'masih', 'split'] as const).map((o) => <button key={o} type="button" disabled={!!busy} title={o === 'shane' ? '' : `Masih ${USUAL[o]}%`} onClick={() => signed(() => saveOne(p, o))}>{busy === p.product_id ? '…' : o === 'shane' ? 'Shane' : `${label(o)} ${USUAL[o]}%`}</button>)}
                  <button type="button" disabled={!!busy} onClick={() => setEdit({ id: p.product_id, owner: 'split', consignorId: '' })}>Other split…</button>
                  <button type="button" disabled={!!busy} onClick={() => setEdit({ id: p.product_id, owner: 'consignor', consignorId: allCons.find((c) => c.key === p.consignor_key && p.consignor_key !== '0001')?.id ?? '' })}>Consignor…</button>
                  <button type="button" className="rp-ocard__link" disabled={!!busy} title="Duplicate, not tracked, not here" onClick={() => setOos(p.product_id)}>Out of stock</button>
                </div>
              )}
              {edit?.id === p.product_id && (
                <OwnerEditor key={edit.owner + edit.id} owners={['split', 'masih', 'consignor']} initial={edit} consignors={allCons} onAdded={onAdded} signer={sg}
                  onSave={(o, cid, pct) => saveEdit(p, o, cid, pct)} onCancel={() => setEdit(null)} />
              )}
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && <div className="rp-empty">{show === 'review' ? 'Nothing left to review' : 'Nothing here: every piece has its owner'}</div>}
      </div>
      {rows.length > shown && <button type="button" className="rp-btn rp-more" onClick={() => setShown((x) => x + 48)}>Show more ({int(rows.length - shown)} left)</button>}
      {sel.size > 0 && (
        <div className="rp-bulkbar">
          <b>{int(sel.size)} selected</b>
          {show === 'review' && <Button primary disabled={!!busy} onClick={() => signed(() => confirm([...sel]))}>{busy.startsWith('bulk') ? 'Saving…' : `Mark ${int(sel.size)} correct`}</Button>}
          {bulkOos ? (
            <div className="rp-bulkbar__seg" role="group" aria-label="Why out of stock">
              {OOS_REASONS.map(([k, l]) => <button key={k} type="button" disabled={!!busy} onClick={() => signed(() => outOfStock([...sel], k))}>{l}</button>)}
              <button type="button" onClick={() => setBulkOos(false)}>Cancel</button>
            </div>
          ) : <button type="button" className="rp-bulkbar__oos" disabled={!!busy} onClick={() => setBulkOos(true)}>Out of stock…</button>}
          <span className="rp-bulkbar__or">or set the owner</span>
          <div className="rp-bulkbar__seg" role="group" aria-label="Owner">
            {OWNERS.map((o) => <button key={o} type="button" aria-pressed={bulkOwner === o} onClick={() => { setBulkOwner(o); setSplit('') }}>{label(o)}</button>)}
          </div>
          {needMasihPct && (
            <label className="rp-bulkbar__split">Masih %<input inputMode="numeric" value={split} placeholder={String(USUAL[bulkOwner])} onChange={(e) => setSplit(e.target.value.replace(/[^0-9.]/g, '').slice(0, 5))} aria-label="Masih's share" /></label>
          )}
          {needConsignor && (
            <>
              <ConsignorSelect className="rp-bulkbar__select" list={allCons} value={consignorId} onChange={(id) => { setConsignorId(id); setSplit('') }} onAdded={onAdded} signer={sg} />
              <label className="rp-bulkbar__split">Their %<input inputMode="numeric" value={split} placeholder={consignor?.default_split != null ? String(n(consignor.default_split)) : '…'} onChange={(e) => setSplit(e.target.value.replace(/[^0-9.]/g, '').slice(0, 5))} aria-label="Consignor split" /></label>
            </>
          )}
          <span className="rp-bulkbar__note">{needMasihPct ? `Masih's share in Consignify: ${splitVal ?? USUAL[bulkOwner]}%${splitVal == null ? ' (the usual)' : ''}. ` : ''}Writes the Shopify tag, the Consignify record and an audit line. The same call QA makes.</span>
          {signer ? <Badge tone="green">Signing as {signer.name}</Badge> : null}
          <Button primary={show !== 'review'} disabled={!!busy || splitBad || (needConsignor && (!consignorId || splitVal == null || !(splitVal > 0 && splitVal < 100)))} onClick={() => signed(saveBulk)}>
            {busy.startsWith('bulk') ? `Saving ${busy.slice(5)}…` : `Save ${int(sel.size)} as ${needConsignor && consignor ? consignor.name || consignor.key : label(bulkOwner)}${needMasihPct && splitVal != null ? ` (Masih ${splitVal}%)` : ''}`}
          </Button>
        </div>
      )}
      {sg.askEl}
      {open && <PieceDrawer productId={open} onClose={() => setOpen(null)} onChanged={refresh} />}
    </Screen>
  )
}
